import {
  resolveVerticalStack,
  type Rect,
} from "@yjsgames/phaser-ui/panel-content";
import type { TextDirection } from "@yjsgames/i18n";
import { resolveOriginX } from "./directionalUi.js";
import codexSpec from "../data/layouts/codex.json";
import { CRAZY_GAMES_INLINE_BANNER_SLOT } from "../ads/CrazyGamesBannerSlot.js";

// 도감 레이아웃 — tip(상단 고정) + scroll(fill) + filter(하단 고정) + 2열 그리드 카드 셀(스크롤 로컬좌표).
// statUpgradeLayout(scroll fill) + dailyShopLayout(그리드 셀) 패턴 결합. 신규 추상화 0.
const SPEC = codexSpec as unknown as {
  titleH: number;
  scrollGapTop: number;
  listBottomInset: number;
  cols: number;
  cardH: number;
  pad: number;
  gutter: number;
  poolCellPx: number;
  poolGap: number;
  poolTopDy: number;
  nameDy: number;
  descDy: number;
  descPadX: number;
};

/** 상단 팁 캐러셀 영역 — 도감 제목은 서브탭과 중복되어 예약하지 않는다. */
const TIP_H = 40;
const FILTER_H = 26;
const FILTER_GAP = 8;

/** 카드 1장 좌표 — 모두 ScrollableList 로컬좌표(viewport 원점 기준). frame은 origin 0,0, 텍스트/pool은 cx 중심정렬. */
export interface CodexCardLayout {
  /** 카드 배경 프레임 (origin 0,0). */
  readonly frame: Rect;
  /** 카드 중심 x — pool 미니셀/이름/등급 텍스트 중앙정렬 기준. */
  readonly cx: number;
  /** pool 미니셀 행 중심 y. */
  readonly poolY: number;
  /** 이름 텍스트 중심 y. */
  readonly nameY: number;
  /** 이름 텍스트 자동 줄바꿈 폭 (카드 폭 - 좌우 패딩) — 긴 이름의 가로 넘침 차단. */
  readonly nameWrapW: number;
  /** 짧은 설명 텍스트 상단 y (2줄 wordWrap, origin 0.5/0). */
  readonly descY: number;
  /** 설명 텍스트 자동 줄바꿈 폭 (카드 폭 - 좌우 패딩). */
  readonly descWrapW: number;
}

export interface CodexLayout {
  /** 과거 제목 영역 호환 필드. 현재 도감 제목은 서브탭과 중복되어 공간을 예약하지 않는다. */
  readonly header: Rect;
  /** 제목 아래 팁 캐러셀 영역 (전체폭) — TipCarouselView 배치 기준(스탯 탭 동형). */
  readonly tip: Rect;
  /** 모듈 도감 3상태 필터 바 영역. */
  readonly filter: Rect;
  /** 헤더 텍스트 origin x — LTR 0(좌)/RTL 1(우). */
  readonly textOriginX: number;
  /** 스크롤 viewport 영역 (header 아래 fill, 패널 하단 inset까지). */
  readonly scroll: Rect;
  /** 카드 셀 좌표 (스크롤 로컬). */
  readonly cards: readonly CodexCardLayout[];
  /** CrazyGames 웹 inline responsive 배너 슬롯 (스크롤 콘텐츠 로컬 좌표). */
  readonly bannerSlot: { x: number; y: number; width: number; height: number; gapY: number };
  /** 스크롤 콘텐츠 총 높이 = ceil(count/cols)*cardH. */
  readonly contentHeight: number;
  /** pool 미니셀 한 칸 px. */
  readonly poolCellPx: number;
  /** pool 미니셀 간 간격 px. */
  readonly poolGap: number;
}

export interface CodexLayoutOptions {
  readonly inlineBannerEnabled?: boolean;
}

/**
 * 도감 탭 좌표 (순수). tip(고정) + scroll(fill)을 resolveVerticalStack으로 누적해 세로 침범을 구조적으로
 * 차단하고(scroll.y+height ≤ panelBottom), scroll 폭 안에서 2열 그리드 카드 셀을 로컬좌표로 배치한다.
 * 스크롤바 거터(gutter)를 폭에서 차감해 카드가 thumb과 겹치지 않게 한다. rectsOverlap 단위테스트가 침범 회귀를 정적 검증.
 * @param content 탭 패널 콘텐츠 영역 Rect — 스크롤 viewport의 x/y/width 기준.
 * @param count 카드 수(ALL_MODULE_DEFINITIONS.length).
 * @param panelBottom LAYOUT.tabPanelBottom — 스크롤 영역 하단 기준(이 값을 넘지 않음).
 */
export function computeCodexLayout(
  content: Rect,
  count: number,
  panelBottom: number,
  dir: TextDirection = "ltr",
  options: CodexLayoutOptions = {},
): CodexLayout {
  const rtl = dir === "rtl";
  const stackBottom = panelBottom;
  const stack = resolveVerticalStack(
    {
      children: [
        { id: "title", height: 0 },
        { id: "tip", height: TIP_H },
        { id: "scroll", fill: true, gapTop: SPEC.scrollGapTop },
        { id: "filter", height: FILTER_H, gapTop: FILTER_GAP },
      ],
    },
    { x: content.x, y: content.y, width: content.width, height: stackBottom - content.y },
  );
  // 헤더는 호환 필드로만 남긴다. 실제 제목은 숨김 처리되며 top 공간도 예약하지 않는다.
  const titleRect = { ...stack.title!, width: content.width, height: 0 };
  const header = rtl ? { ...titleRect, x: titleRect.x + titleRect.width } : titleRect;
  const tipRect = stack.tip!;
  const filterRect = stack.filter!;
  const scroll = stack.scroll!;

  // 그리드 셀 폭 — 스크롤바 거터를 빼고 cols로 균등 분할(카드가 thumb과 겹치지 않게).
  const cellW = Math.floor((scroll.width - SPEC.gutter) / SPEC.cols);
  const inlineBannerEnabled = options.inlineBannerEnabled ?? true;
  const bannerWidth = inlineBannerEnabled
    ? Math.min(scroll.width, Math.max(scroll.width - SPEC.gutter, CRAZY_GAMES_INLINE_BANNER_SLOT.minWidth))
    : 0;
  const bannerSlot = {
    x: Math.max(0, Math.floor((scroll.width - bannerWidth) / 2)),
    y: SPEC.cardH + (inlineBannerEnabled ? CRAZY_GAMES_INLINE_BANNER_SLOT.gapY : 0),
    width: bannerWidth,
    height: inlineBannerEnabled ? CRAZY_GAMES_INLINE_BANNER_SLOT.height : 0,
    gapY: inlineBannerEnabled ? CRAZY_GAMES_INLINE_BANNER_SLOT.gapY : 0,
  };
  const bannerOffset = bannerSlot.height + bannerSlot.gapY * 2;
  const cards: CodexCardLayout[] = [];
  for (let i = 0; i < count; i++) {
    const col = i % SPEC.cols;
    // RTL이면 열 순서를 좌우 반전 — 첫 카드가 우측 열부터(dailyShop 패턴).
    const mcol = rtl ? SPEC.cols - 1 - col : col;
    const row = Math.floor(i / SPEC.cols);
    const cellX = mcol * cellW;
    const cellY = row * SPEC.cardH + (row >= 1 ? bannerOffset : 0);
    // 텍스트 줄바꿈 폭 — 카드 폭에서 좌우 패딩을 뺀 안쪽. name/desc 모두 이 폭으로 줄바꿈해 가로 넘침을 차단.
    const innerW = cellW - SPEC.pad - SPEC.descPadX * 2;
    cards.push({
      frame: {
        x: cellX + SPEC.pad / 2,
        y: cellY + SPEC.pad / 2,
        width: cellW - SPEC.pad,
        height: SPEC.cardH - SPEC.pad,
      },
      cx: cellX + cellW / 2,
      poolY: cellY + SPEC.poolTopDy,
      nameY: cellY + SPEC.nameDy,
      nameWrapW: innerW,
      descY: cellY + SPEC.descDy,
      descWrapW: innerW,
    });
  }

  return {
    header,
    tip: { x: content.x, y: tipRect.y, width: content.width, height: tipRect.height },
    filter: { x: content.x, y: filterRect.y, width: content.width, height: filterRect.height },
    textOriginX: resolveOriginX(dir),
    scroll,
    cards,
    bannerSlot,
    contentHeight: Math.ceil(count / SPEC.cols) * SPEC.cardH + bannerOffset,
    poolCellPx: SPEC.poolCellPx,
    poolGap: SPEC.poolGap,
  };
}
